// Typed Clients API client for the web layer — the Account tier
// (three-tier client model Stage 3, ADR-0015 D2).
//
// Wraps the read-only `clientsContract` endpoint via the shared `api`
// client and exposes react-query hooks. There are NO mutation hooks:
// Account is ingest-written only (no Actions exist by design).
//
// The tier has no pages of its own any more: the Account LIST is the
// tri-tier Client Book table, and one Account's identity hangs off its
// client's Client Book record. What is left here serves the relationship
// counterpart picker, which still needs to search Accounts by name.

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AccountStatus, ClientSearchResponse, ListAccountsResponse } from '@septima/contract';

export interface AccountsQuery {
    search?: string;
    status?: AccountStatus;
}

export const clientsKeys = {
    all: ['accounts'] as const,
    list: (q?: AccountsQuery) => ['accounts', 'list', q ?? {}] as const,
};

export function useAccounts(query?: AccountsQuery, options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: clientsKeys.list(query),
        enabled: options?.enabled ?? true,
        queryFn: async (): Promise<ListAccountsResponse> => {
            const res = await api.accounts.listAccounts({ query: query ?? {} });
            if (res.status !== 200) throw new Error(`Clients failed: ${res.status}`);
            return res.body;
        },
        staleTime: 10_000,
    });
}

export interface ClientSearchQuery {
    search?: string;
}

export const clientSearchKeys = {
    all: ['clients'] as const,
    list: (q?: ClientSearchQuery) => ['clients', 'list', q ?? {}] as const,
};

/**
 * The registered-holder type-ahead behind the relationship counterpart picker.
 *
 * The server scopes the rows to the caller's book and caps the list, so the
 * hook passes the typed text straight through rather than holding the whole
 * tier and filtering in the browser: a Client name is the only thing the search
 * matches, and the Account hook's "hold the book" trick is not available to a
 * tier this large.
 */
export function useClientSearch(query?: ClientSearchQuery, options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: clientSearchKeys.list(query),
        enabled: options?.enabled ?? true,
        queryFn: async (): Promise<ClientSearchResponse> => {
            const res = await api.clients.searchClients({ query: query ?? {} });
            if (res.status !== 200) throw new Error(`Client search failed: ${res.status}`);
            return res.body;
        },
        staleTime: 10_000,
    });
}
